import React, { useState, useMemo, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Lock, CreditCard, ChevronLeft, CheckCircle2, AlertCircle, ShieldCheck, Tag, X } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { storeApi, formatPrice, formatStoreError } from "@/lib/storefront";
import SEO from "@/components/storefront/SEO";
import { toast } from "sonner";

const PROMO_KEY = "store_promo_v1";

function readPromo() {
  try {
    const raw = localStorage.getItem(PROMO_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    console.warn("Promo read failed", err?.message || err);
    return null;
  }
}

function writePromo(p) {
  try {
    if (p) localStorage.setItem(PROMO_KEY, JSON.stringify(p));
    else localStorage.removeItem(PROMO_KEY);
  } catch (err) {
    console.warn("Promo write failed", err?.message || err);
  }
}

const EMPTY = {
  customer_email: "",
  customer_name: "",
  customer_phone: "",
  shipping_address: {
    recipient: "", phone: "", country: "United States", region: "",
    city: "", zip: "", street: "",
  },
  shipping_method: "standard",
  shipping_cost: 8,
  payment_method: "card",
  promo_code: "",
};

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const nav = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [card, setCard] = useState({ number: "", exp: "", cvc: "", name: "" });
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [promo, setPromo] = useState(() => readPromo());
  const [promoCode, setPromoCode] = useState("");
  const [promoBusy, setPromoBusy] = useState(false);
  const [promoError, setPromoError] = useState("");
  const currency = items[0]?.currency || "USD";

  const shippingOptions = [
    { v: "standard", l: "Standard", desc: "5–7 business days", cost: subtotal >= 75 ? 0 : 8 },
    { v: "express", l: "Express", desc: "2–3 business days", cost: 18 },
  ];

  const selected = shippingOptions.find((o) => o.v === form.shipping_method) || shippingOptions[0];
  const freeShip = !!promo?.free_shipping;
  const shippingCost = freeShip ? 0 : selected.cost;
  const discount = useMemo(() => {
    if (!promo || freeShip) return 0;
    if (promo.type === "percent") return +(subtotal * (promo.value / 100)).toFixed(2);
    if (promo.type === "fixed") return +Math.min(promo.value, subtotal).toFixed(2);
    return 0;
  }, [promo, subtotal, freeShip]);
  const total = useMemo(() => Math.max(0, subtotal - discount + shippingCost), [subtotal, discount, shippingCost]);

  // Sync promo into the form payload
  useEffect(() => {
    setForm((f) => ({ ...f, promo_code: promo?.code || "" }));
  }, [promo]);

  const applyPromo = async (e) => {
    e?.preventDefault();
    if (!promoCode.trim()) return;
    setPromoBusy(true);
    setPromoError("");
    try {
      const { data } = await storeApi.post("/promo/validate", { code: promoCode.trim(), subtotal });
      setPromo(data);
      writePromo(data);
      setPromoCode("");
    } catch (err) {
      setPromoError(formatStoreError(err));
    } finally {
      setPromoBusy(false);
    }
  };

  const removePromo = () => {
    setPromo(null);
    writePromo(null);
    setPromoError("");
  };

  if (items.length === 0 && !processing) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 text-center sm:px-6 sm:py-24" data-testid="checkout-empty">
        <h1 className="font-display text-3xl sm:text-4xl md:text-5xl">Nothing to checkout</h1>
        <p className="mt-3 text-[#2D2823]">Your bag is empty.</p>
        <Link to="/catalog" className="mt-8 inline-block rounded-full bg-[#1A1A1A] px-6 py-3 text-sm text-white">Explore the shop</Link>
      </div>
    );
  }

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setAddr = (k, v) => setForm((f) => ({ ...f, shipping_address: { ...f.shipping_address, [k]: v } }));

  const validate = () => {
    if (!form.customer_email || !form.customer_email.includes("@")) return "Enter a valid email";
    if (!form.customer_name) return "Enter your name";
    if (!form.shipping_address.city || !form.shipping_address.street) return "Enter your shipping address";
    if (!card.number || card.number.replace(/\s/g, "").length < 13) return "Enter your card number";
    if (!card.exp || !/^\d{2}\/\d{2}$/.test(card.exp)) return "Enter card expiry (MM/YY)";
    if (!card.cvc || card.cvc.length < 3) return "Enter card CVC";
    return null;
  };

  const pay = async (e) => {
    e.preventDefault();
    setError("");
    const err = validate();
    if (err) { setError(err); toast.error(err); return; }
    setProcessing(true);

    try {
      // Create payment intent (Stripe-like)
      await storeApi.post("/checkout/payment-intent", {
        amount: total,
        currency,
      });

      // Simulate Stripe tokenization delay for realism
      await new Promise((r) => setTimeout(r, 1400));

      // Submit order — backend marks as payment_error for MVP
      const payload = {
        customer_email: form.customer_email,
        customer_name: form.customer_name,
        customer_phone: form.customer_phone,
        items: items.map((it) => ({
          product_id: it.product_id,
          title: it.title,
          image: it.image,
          price: it.price,
          quantity: it.quantity,
          subtotal: it.price * it.quantity,
          asin: it.asin,
          sku: it.sku,
        })),
        shipping_address: form.shipping_address,
        shipping_method: selected.l,
        shipping_cost: shippingCost,
        payment_method: form.payment_method,
        payment_status: "failed",
        status: "payment_error",
        promo_code: promo?.code || "",
        discount,
        currency,
      };
      const { data } = await storeApi.post("/orders", payload);

      // For MVP: always surface the payment_declined screen, order exists in admin for manual processing
      clear();
      writePromo(null);
      nav(`/order/${data.id}?email=${encodeURIComponent(form.customer_email)}`, { replace: true });
    } catch (err) {
      setError(formatStoreError(err));
      toast.error(formatStoreError(err));
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F2EDDF]" data-testid="checkout-page">
      <SEO title="Checkout" noindex />
      {/* Minimal top bar */}
      <div className="border-b border-[#DDD6C5] bg-white">
        <div className="mx-auto flex max-w-[1200px] flex-wrap items-center justify-between gap-2 px-4 py-4 sm:px-6 sm:py-5 md:px-10">
          <Link to="/cart" className="inline-flex items-center gap-1 text-sm text-[#2D2823] hover:text-[#1A1A1A]">
            <ChevronLeft size={16} /> Back to bag
          </Link>
          <Link to="/" className="font-display text-base tracking-[0.06em] sm:text-xl sm:tracking-[0.08em]">Luna. Creative Home</Link>
          <div className="hidden items-center gap-1.5 text-xs text-[#2D2823] sm:flex">
            <Lock size={13} /> Secure checkout
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-[1200px] grid-cols-1 gap-10 px-4 py-8 sm:px-6 sm:py-10 md:grid-cols-[1fr_420px] md:gap-16 md:px-10 md:py-14">
        {/* Form */}
        <form onSubmit={pay} className="min-w-0 space-y-8">
          {/* Contact */}
          <section>
            <h2 className="mb-4 font-display text-2xl">Contact</h2>
            <div className="grid gap-3 md:grid-cols-2">
              <Input label="Email" type="email" value={form.customer_email} onChange={(v) => setField("customer_email", v)} required testid="co-email" />
              <Input label="Phone (optional)" value={form.customer_phone} onChange={(v) => setField("customer_phone", v)} testid="co-phone" />
              <Input label="Full name" value={form.customer_name} onChange={(v) => setField("customer_name", v)} required testid="co-name" className="md:col-span-2" />
            </div>
          </section>

          {/* Shipping */}
          <section>
            <h2 className="mb-4 font-display text-2xl">Shipping address</h2>
            <div className="grid gap-3 md:grid-cols-2">
              <Input label="Country" value={form.shipping_address.country} onChange={(v) => setAddr("country", v)} />
              <Input label="Region / State" value={form.shipping_address.region} onChange={(v) => setAddr("region", v)} />
              <Input label="City" value={form.shipping_address.city} onChange={(v) => setAddr("city", v)} required testid="co-city" />
              <Input label="ZIP / Postal code" value={form.shipping_address.zip} onChange={(v) => setAddr("zip", v)} />
              <Input label="Street address" value={form.shipping_address.street} onChange={(v) => setAddr("street", v)} required testid="co-street" className="md:col-span-2" />
            </div>
          </section>

          {/* Shipping method */}
          <section>
            <h2 className="mb-4 font-display text-2xl">Delivery method</h2>
            <div className="space-y-2">
              {shippingOptions.map((opt) => (
                <label key={opt.v} className={`flex cursor-pointer items-center justify-between rounded-md border px-4 py-3 transition-colors ${form.shipping_method === opt.v ? "border-[#1A1A1A] bg-white" : "border-[#DDD6C5] hover:bg-white"}`}>
                  <div className="flex items-center gap-3">
                    <input type="radio" checked={form.shipping_method === opt.v} onChange={() => setField("shipping_method", opt.v)} className="accent-[#1A1A1A]" />
                    <div>
                      <div className="text-sm font-medium">{opt.l}</div>
                      <div className="text-xs text-[#2D2823]">{opt.desc}</div>
                    </div>
                  </div>
                  <div className="text-sm font-medium store-nums">{opt.cost === 0 ? "Free" : formatPrice(opt.cost, currency)}</div>
                </label>
              ))}
            </div>
          </section>

          {/* Payment — Stripe-like UI */}
          <section>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="font-display text-2xl">Payment</h2>
              <div className="flex items-center gap-1.5 text-xs text-[#2D2823]">
                <ShieldCheck size={13} /> Encrypted
              </div>
            </div>

            <div className="rounded-md border border-[#DDD6C5] bg-white p-5">
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm">
                  <CreditCard size={16} className="text-[#2D2823]" />
                  <span>Credit or debit card</span>
                </div>
                <div className="flex items-center gap-1 text-xs text-[#2D2823] font-mono uppercase tracking-wider">visa · mc · amex</div>
              </div>

              <div className="space-y-3">
                <Input label="Card number" value={card.number} onChange={(v) => setCard((c) => ({ ...c, number: formatCard(v) }))} placeholder="4242 4242 4242 4242" required testid="co-card-number" mono />
                <div className="grid gap-3 md:grid-cols-3">
                  <Input label="Expiry (MM/YY)" value={card.exp} onChange={(v) => setCard((c) => ({ ...c, exp: formatExp(v) }))} placeholder="12/28" required testid="co-card-exp" mono />
                  <Input label="CVC" value={card.cvc} onChange={(v) => setCard((c) => ({ ...c, cvc: v.replace(/\D/g, "").slice(0, 4) }))} placeholder="123" required testid="co-card-cvc" mono />
                  <Input label="Name on card" value={card.name} onChange={(v) => setCard((c) => ({ ...c, name: v }))} className="md:col-span-1" />
                </div>
              </div>

              <p className="mt-4 flex items-start gap-2 text-xs text-[#2D2823]">
                <Lock size={12} className="mt-0.5 flex-shrink-0" />
                Your payment is processed securely. We don't store card details on our servers.
              </p>
            </div>
          </section>

          {error && (
            <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-testid="checkout-error">
              <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={processing}
            className="w-full rounded-full bg-[#1A1A1A] px-6 py-4 text-sm tracking-wide text-white transition-colors hover:bg-[#7D8471] disabled:opacity-70"
            data-testid="pay-now-btn"
          >
            {processing ? "Processing payment…" : `Pay ${formatPrice(total, currency)}`}
          </button>
        </form>

        {/* Summary */}
        <aside className="h-fit rounded-md border border-[#DDD6C5] bg-white p-6 md:sticky md:top-6">
          <h3 className="font-display text-xl">Order summary</h3>
          <ul className="mt-5 space-y-3 border-b border-[#DDD6C5] pb-5">
            {items.map((it) => (
              <li key={it.product_id} className="flex items-center gap-3 text-sm">
                <div className="relative h-16 w-12 overflow-hidden rounded bg-[#E8E2D2]">
                  {it.image && <img src={it.image} alt="" className="h-full w-full object-cover" />}
                  <span className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-[#1A1A1A] text-[10px] text-white store-nums">{it.quantity}</span>
                </div>
                <div className="flex-1 truncate text-xs text-[#1A1A1A]">{it.title}</div>
                <div className="text-xs store-nums">{formatPrice(it.price * it.quantity, it.currency)}</div>
              </li>
            ))}
          </ul>
          <dl className="mt-4 space-y-1.5 text-sm">
            <div className="flex justify-between"><dt className="text-[#2D2823]">Subtotal</dt><dd className="store-nums">{formatPrice(subtotal, currency)}</dd></div>
            {discount > 0 && (
              <div className="flex justify-between text-[#7D8471]" data-testid="checkout-promo-discount">
                <dt>Promo {promo?.code}</dt>
                <dd className="store-nums">−{formatPrice(discount, currency)}</dd>
              </div>
            )}
            <div className="flex justify-between"><dt className="text-[#2D2823]">Shipping</dt><dd className="store-nums">{shippingCost === 0 ? (freeShip ? <span className="text-[#7D8471]">Free (promo)</span> : "Free") : formatPrice(shippingCost, currency)}</dd></div>
            <div className="flex justify-between"><dt className="text-[#2D2823]">Tax</dt><dd className="text-xs text-[#2D2823]">Calculated at shipping</dd></div>
          </dl>
          <div className="mt-4 flex items-baseline justify-between border-t border-[#DDD6C5] pt-4">
            <span className="font-display text-lg">Total</span>
            <span className="font-display store-nums text-2xl" data-testid="checkout-total">{formatPrice(total, currency)}</span>
          </div>

          {/* Promo code block */}
          <div className="mt-5 border-t border-[#DDD6C5] pt-5">
            {promo ? (
              <div className="flex items-center justify-between gap-2 rounded-md bg-[#E8E2D2] px-3 py-2.5" data-testid="checkout-promo-applied">
                <div className="flex items-center gap-2 text-sm">
                  <Tag size={14} className="text-[#7D8471]" />
                  <div>
                    <div className="font-mono text-xs uppercase tracking-wider text-[#1A1A1A]">{promo.code}</div>
                    <div className="text-xs text-[#2D2823]">{promo.label}</div>
                  </div>
                </div>
                <button type="button" onClick={removePromo} aria-label="Remove promo" className="rounded-full p-1.5 text-[#2D2823] hover:bg-white hover:text-[#1A1A1A]" data-testid="checkout-promo-remove">
                  <X size={14} />
                </button>
              </div>
            ) : (
              <div>
                <label className="mb-1.5 block text-[10px] uppercase tracking-[0.2em] text-[#2D2823]">Promo code</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={promoCode}
                    onChange={(e) => { setPromoCode(e.target.value); if (promoError) setPromoError(""); }}
                    placeholder="Code"
                    className="flex-1 rounded-full border border-[#DDD6C5] bg-white px-3 py-2 text-sm uppercase tracking-wider outline-none focus:border-[#1A1A1A]"
                    data-testid="checkout-promo-input"
                    autoComplete="off"
                  />
                  <button
                    type="button"
                    onClick={applyPromo}
                    disabled={promoBusy || !promoCode.trim()}
                    className="flex-shrink-0 rounded-full border border-[#1A1A1A] px-4 py-2 text-sm text-[#1A1A1A] transition-colors hover:bg-[#1A1A1A] hover:text-white disabled:opacity-40"
                    data-testid="checkout-promo-apply"
                  >
                    {promoBusy ? "..." : "Apply"}
                  </button>
                </div>
                {promoError && <div className="mt-2 text-xs text-rose-600" data-testid="checkout-promo-error">{promoError}</div>}
              </div>
            )}
          </div>

        </aside>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", required, placeholder, testid, className = "", mono }) {
  return (
    <div className={className}>
      <label className="mb-1 block text-xs uppercase tracking-[0.15em] text-[#2D2823]">{label}{required ? " *" : ""}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        data-testid={testid}
        className={`w-full rounded-md border border-[#DDD6C5] bg-white px-4 py-3 text-sm outline-none ring-offset-0 transition-colors placeholder:text-[#2D2823]/40 focus:border-[#1A1A1A] ${mono ? "font-mono" : ""}`}
      />
    </div>
  );
}

function formatCard(v) {
  const digits = v.replace(/\D/g, "").slice(0, 19);
  return digits.replace(/(\d{4})(?=\d)/g, "$1 ");
}
function formatExp(v) {
  const digits = v.replace(/\D/g, "").slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
}
